import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import {
  AuthenticationModel,
  CommonAuthProcessProps,
  EnrollModel,
  SetupModel
} from '@models'
import { AuthenticationStatus, AuthenticationStep } from '@enums'
import ApiService from '@utils/payer-auth-api'
import AuthenticationApi from '@utils/authentication-api'

const usePaywayAuth = ({
  initialAuthentication,
  showBrand
}: CommonAuthProcessProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [showResult, setShowResult] = useState<boolean>(false)
  const [step, setStep] = useState<AuthenticationStep>(AuthenticationStep.Setup)
  const [authentication, setAuthentication] = useState<AuthenticationModel>()
  const [setup, setSetup] = useState<SetupModel>()
  const [enroll, setEnroll] = useState<EnrollModel>()

  const navigate = useNavigate()
  const { id } = useParams()
  const checkedId = id ?? ''

  const handleError = (error?: unknown) => {
    if (error) Sentry.captureException(error)

    navigate('/error', { state: { showBrand } })
  }

  const checkIfNotificationFormIsDisplayed = (
    currentAuthenticationValues: AuthenticationModel
  ) => {
    const externalReturnUrl =
      currentAuthenticationValues?.externalReturnUrl ?? ''
    const isRedirectNotification = externalReturnUrl.trim() !== ''

    if (isRedirectNotification) setStep(AuthenticationStep.Notification)
    else {
      emitEventResult(currentAuthenticationValues)
      setShowResult(true)
    }
  }

  const getSetupPayerParams = async () => {
    setIsLoading(true)
    try {
      const responseSetup = await ApiService.post<SetupModel>(
        `setup/${checkedId}`,
        {}
      )

      if (!responseSetup) {
        handleError()
        return
      }

      setSetup(responseSetup)
      setAuthentication(responseSetup.authentication)

      if (responseSetup.authentication.status !== AuthenticationStatus.PENDING)
        checkIfNotificationFormIsDisplayed(responseSetup.authentication)
      else setStep(AuthenticationStep.Enroll)
    } catch (error) {
      handleError(error)
    }
    setIsLoading(false)
  }

  const checkDeviceDataCollectorResult = async (_: any) => {
    setIsLoading(true)
    try {
      const response = await ApiService.post<EnrollModel>(
        `enroll/${checkedId}`,
        {
          referenceId: '',
          authenticationId: checkedId
        }
      )

      if (!response) {
        handleError()
        return
      }

      if (response.authentication.status === AuthenticationStatus.PENDING) {
        setStep(AuthenticationStep.StepUp)
      } else {
        checkIfNotificationFormIsDisplayed(response.authentication)
      }

      setEnroll(response)
      setAuthentication(response.authentication)
    } catch (error) {
      handleError(error)
    }
    setIsLoading(false)
  }

  const checkAuthenticationResult = async (result: any) => {
    let checkedResult = result ?? {}

    setIsLoading(true)
    try {
      const response = await ApiService.post<AuthenticationModel>(
        `confirmation/payway/${checkedId}`,
        {
          ...checkedResult
        }
      )

      if (!response) {
        handleError()
        return
      }

      checkIfNotificationFormIsDisplayed(response)
      setAuthentication(response)
    } catch (error) {
      handleError(error)
    }
    setIsLoading(false)
  }

  const emitEventResult = (
    currentAuthenticationValues: AuthenticationModel
  ) => {
    AuthenticationApi.emitAuthenticationResult(currentAuthenticationValues)
  }

  useEffect(() => {
    if (
      initialAuthentication &&
      initialAuthentication.status !== AuthenticationStatus.PENDING
    ) {
      setAuthentication(initialAuthentication)
      setIsLoading(false)
      checkIfNotificationFormIsDisplayed(initialAuthentication)
    } else {
      getSetupPayerParams()
    }
  }, [])

  return {
    isLoading,
    showResult,
    authentication,
    step,
    setup,
    enroll,
    checkDeviceDataCollectorResult,
    checkAuthenticationResult
  }
}

export default usePaywayAuth
